import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  CircularProgress
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import FileDropZone from '../components/commun/FileDropZone';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import validerDemande from '../validerFormulaire';
import { isLegrandUser } from '../utils/userUtils';

// Définition du type des paramètres de la route
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number | null;
  url_flag?: string;
  validite?: string;
  commentaires?: string;
}

// Définition du type d'un voyageur
interface Voyageur {
  id: number;
  prenom: string;
  nom: string;
  url?: string;
  numeropax?: string;
}

const FormAngola: React.FC = () => {
  // Extraction des données de la route via useLocation
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };

  const routeState = getInitialState();
  const { title, prixconsulat, prixintervention, demandeId, url_flag, validite } = routeState;
  const commentaires = routeState?.commentaires || '';
  const isEditMode = demandeId != null;

  // États du formulaire
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);
  const [hideFeesForLegrand] = useState<boolean>(isLegrandUser());
  const [formTitle, setFormTitle] = useState<string>(title || '');
  const [consulatPrice, setConsulatPrice] = useState<number>(prixconsulat || 0);
  const [interventionPrice, setInterventionPrice] = useState<number>(prixintervention || 0);
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageur[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [certificatVaccinationFile, setCertificatVaccinationFile] = useState<File | null>(null);
  const [reservationHotelFile, setReservationHotelFile] = useState<File | null>(null);
  const [billetAvionFile, setBilletAvionFile] = useState<File | null>(null);
  const [releveBancaireFile, setReleveBancaireFile] = useState<File | null>(null);
  const [profession, setProfession] = useState<string>('');
  const [nomContactAngola, setNomContactAngola] = useState<string>('');
  const [numeroCarteIdentiteContact, setNumeroCarteIdentiteContact] = useState<string>('');
  const [dateDelivranceCarteIdentiteContact, setDateDelivranceCarteIdentiteContact] = useState<string>('');
  const [dateExpirationCarteIdentiteContact, setDateExpirationCarteIdentiteContact] = useState<string>('');
  const [emailContactAngola, setEmailContactAngola] = useState<string>('');
  const [pointControleArrivee, setPointControleArrivee] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);

  const prixTotal = consulatPrice + interventionPrice;

  // Mise à jour des champs non remplis
  const updateChampsNonRemplis = useCallback(() => {
    const champs: string[] = [];
    if (!selectedVoyageur) champs.push('Voyageur');
    if (!dateDepart.trim()) champs.push('Date de départ');
    if (!dateSortie.trim()) champs.push('Date de sortie');
    if (!profession.trim()) champs.push('Profession');
    if (!nomContactAngola.trim()) champs.push('Nom et prénom du contact en Angola');
    if (!numeroCarteIdentiteContact.trim()) champs.push("N° de carte d'identité du contact en Angola");
    if (!dateDelivranceCarteIdentiteContact.trim()) champs.push("Date de délivrance de la carte d'identité du contact");
    if (!dateExpirationCarteIdentiteContact.trim()) champs.push("Date d'expiration de la carte d'identité du contact");
    if (!emailContactAngola.trim()) champs.push("E-mail du contact en Angola");
    if (!pointControleArrivee.trim()) champs.push("Point de contrôle d’arrivée en Angola");
    if (!assuranceVisa.trim()) champs.push("Assurance Visa");
    if (!certificatVaccinationFile) champs.push("Certificat international de vaccination");
    if (!reservationHotelFile) champs.push("Réservation hôtelière ou lettre d'invitation");
    if (!billetAvionFile) champs.push("Billet d'avion Aller / Retour");
    if (!releveBancaireFile) champs.push("Relevé bancaire");
    if (!sliderValue) champs.push("Engagement sur les conditions générales de vente");
    setChampsNonRemplis(champs);
  }, [
    selectedVoyageur,
    dateDepart,
    dateSortie,
    profession,
    nomContactAngola,
    numeroCarteIdentiteContact,
    dateDelivranceCarteIdentiteContact,
    dateExpirationCarteIdentiteContact,
    emailContactAngola,
    pointControleArrivee,
    assuranceVisa,
    certificatVaccinationFile,
    reservationHotelFile,
    billetAvionFile,
    releveBancaireFile,
    sliderValue
  ]);

  useEffect(() => {
    updateChampsNonRemplis();
  }, [updateChampsNonRemplis]);

  // La validation se base sur l'absence de champs manquants
  const isButtonEnabled = champsNonRemplis.length === 0;

  // Récupération de la liste des voyageurs via localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true);
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Formatage de la date en JJ/MM/AAAA
  const formatDate = (input: string): string => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  /**
   * Ce formulaire a toujours envoyé au backend l'URL d'aperçu locale du document,
   * et non le document lui-même : validerFormulaire ne reconvertit en fichier que les
   * valeurs commençant par `data:`, `http` ou `file:`, jamais un `blob:`, qui part donc
   * en simple champ texte. Comportement conservé ici tel quel, le corriger supposant de
   * décider côté serveur sous quel nom de champ recevoir ces quatre pièces jointes.
   */
  const urlApercuLocale = (fichier: File | null) => (fichier ? URL.createObjectURL(fichier) : '');

  // Gestion de la soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = champsNonRemplis.length === 0;
      const data = {
        etatFormulaire,
        idUtilisateur: userId,
        idVoyageur: selectedVoyageur,
        titreVisa: formTitle,
        dateDepart,
        dateSortie,
        prixconsulat: consulatPrice,
        prixintervention: interventionPrice,
        assuranceVisa,
        certificatVaccinationUri: urlApercuLocale(certificatVaccinationFile),
        reservationHotelUri: urlApercuLocale(reservationHotelFile),
        billetAvionUri: urlApercuLocale(billetAvionFile),
        releveBancaireUri: urlApercuLocale(releveBancaireFile),
        profession,
        nomContactAngola,
        numeroCarteIdentiteContact,
        dateDelivranceCarteIdentiteContact,
        dateExpirationCarteIdentiteContact,
        emailContactAngola,
        pointControleArrivee,
        validite,
      };

      const response = await validerDemande(data);
      console.log('Demande validée avec succès');
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.idDemande) {
        setCreatedDemandeId(response.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setCertificatVaccinationFile(null);
    setReservationHotelFile(null);
    setBilletAvionFile(null);
    setReleveBancaireFile(null);
    setProfession('');
    setNomContactAngola('');
    setNumeroCarteIdentiteContact('');
    setDateDelivranceCarteIdentiteContact('');
    setDateExpirationCarteIdentiteContact('');
    setEmailContactAngola('');
    setPointControleArrivee('');
  };

  // Handler vide si le bouton est désactivé
  const disabledPressHandler = () => {};

  // Prévention de sortie accidentelle (avant déchargement de la page)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Bouton "Retour" avec confirmation
  const handleRetour = () => {
    const confirmation = window.confirm(
      'Si vous retournez en arrière, vous allez perdre les données déjà saisies dans ce formulaire. Êtes-vous sûr ?'
    );
    if (confirmation) {
      navigate(-1);
    }
  };

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {url_flag && (
              <Box
                component="img"
                src={url_flag}
                alt="Flag"
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              Demande enregistree avec succes !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Votre demande de visa pour {formTitle} a bien ete enregistree.
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors societe et hors Legrand) */}
              {isClientSociete === false && !hideFeesForLegrand && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  Payer en ligne
                </Button>
              )}

              {/* Bouton Creer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                Creer une nouvelle demande
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                Voir mes demandes
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {url_flag && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img src={url_flag} alt="Drapeau" style={{ maxWidth: '100%', height: 'auto' }} />
        </Box>
      )}
      <Typography variant="h4" gutterBottom>
        {`Formulaire pour ${formTitle}`}
      </Typography>
      <Typography variant="subtitle1" gutterBottom>{`Prix : ${prixTotal}€`}</Typography>

      {/* Affichage des commentaires du visa en HTML */}
      {commentaires && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: '#fff3cd',
            borderRadius: 1,
            border: '1px solid #ffc107',
          }}
          dangerouslySetInnerHTML={{ __html: commentaires }}
        />
      )}

      {!isEditMode && (
        <Box sx={{ my: 2 }}>
          <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
            Sélectionner un voyageur
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="voyageur-select-label">Voyageur</InputLabel>
            <Select
              labelId="voyageur-select-label"
              value={selectedVoyageur}
              label="Voyageur"
              onChange={(e) => setSelectedVoyageur(e.target.value as string)}
            >
              {voyageurs.map((v) => (
                <MenuItem key={v.id} value={v.id.toString()}>
                  {`${v.prenom} ${v.nom}${v.numeropax ? ` - Passeport: ${v.numeropax}` : ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Bouton pour créer un nouveau voyageur */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                sessionStorage.setItem('formState', JSON.stringify({
                  title,
                  prixconsulat,
                  prixintervention,
                  url_flag,
                  validite,
                  commentaires
                }));
                navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
              }}
              sx={{
                color: '#1976d2',
                borderColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#1565c0'
                }
              }}
            >
              Créer un nouveau voyageur
            </Button>
          </Box>
        </Box>
      )}

      {selectedVoyageurObj && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <Avatar
            src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
            sx={{ width: 100, height: 100 }}
          />
        </Box>
      )}

      <Box sx={{ my: 2 }}>
        <TextField
          label="Date de départ de votre voyage (JJ/MM/AAAA)"
          fullWidth
          value={dateDepart}
          onChange={(e) => setDateDepart(formatDate(e.target.value))}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="Date de retour de votre voyage (JJ/MM/AAAA)"
          fullWidth
          value={dateSortie}
          onChange={(e) => setDateSortie(formatDate(e.target.value))}
        />
      </Box>

      <FileDropZone
        label="Certificat international de vaccination (fièvre jaune)"
        file={certificatVaccinationFile}
        onFileSelected={setCertificatVaccinationFile}
        required
      />
      <FileDropZone
        label="Réservation hôtelière ou lettre d'invitation"
        file={reservationHotelFile}
        onFileSelected={setReservationHotelFile}
        required
      />
      <FileDropZone
        label="Billet d'avion Aller / Retour"
        file={billetAvionFile}
        onFileSelected={setBilletAvionFile}
        required
      />
      <FileDropZone
        label="Relevé bancaire (200 US$ par jour)"
        file={releveBancaireFile}
        onFileSelected={setReleveBancaireFile}
        required
      />

      <Box sx={{ my: 2 }}>
        <TextField
          label="Profession"
          fullWidth
          value={profession}
          onChange={(e) => setProfession(e.target.value)}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="Nom et prénom du contact en Angola"
          fullWidth
          value={nomContactAngola}
          onChange={(e) => setNomContactAngola(e.target.value)}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="N° de carte d'identité du contact en Angola"
          fullWidth
          value={numeroCarteIdentiteContact}
          onChange={(e) => setNumeroCarteIdentiteContact(e.target.value)}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="Date de délivrance de la carte d'identité du contact (JJ/MM/AAAA)"
          fullWidth
          value={dateDelivranceCarteIdentiteContact}
          onChange={(e) => setDateDelivranceCarteIdentiteContact(formatDate(e.target.value))}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="Date d'expiration de la carte d'identité du contact (JJ/MM/AAAA)"
          fullWidth
          value={dateExpirationCarteIdentiteContact}
          onChange={(e) => setDateExpirationCarteIdentiteContact(formatDate(e.target.value))}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="E-mail du contact en Angola"
          fullWidth
          value={emailContactAngola}
          onChange={(e) => setEmailContactAngola(e.target.value)}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <TextField
          label="Point de contrôle d’arrivée en Angola"
          fullWidth
          value={pointControleArrivee}
          onChange={(e) => setPointControleArrivee(e.target.value)}
        />
      </Box>

      <Box sx={{ my: 2 }}>
        <AssuranceVisaComponent
          assuranceVisa={assuranceVisa}
          setAssuranceVisa={setAssuranceVisa}
          sliderValue={sliderValue}
          setSliderValue={setSliderValue}
        />
      </Box>

      <Box
        sx={{
          my: 2,
          p: 2,
          backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
          borderRadius: 1,
          textAlign: 'center'
        }}
      >
        {champsNonRemplis.length > 0 ? (
          <>
            <Typography variant="body1">Formulaire incomplet. Champs manquants :</Typography>
            {champsNonRemplis.map((champ, index) => (
              <Typography key={index} variant="body2">
                {champ}
              </Typography>
            ))}
          </>
        ) : (
          <Typography variant="body1">Formulaire complet</Typography>
        )}
      </Box>

      <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={handleRetour}>
          Retour
        </Button>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={isButtonEnabled ? handleSubmit : disabledPressHandler}
            disabled={!isButtonEnabled}
          >
            Valider ma demande
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default FormAngola;
